<template>
  <div
    :class="$style.page"
    :style="{
      '--aspectRatio': imageAspectRatio,
    }"
  >
    <div :class="[$style.carouselImage,$style.carouselImageActive, $style.carouselImagePlaceholder]">
      <div :class="$style.carouselContent" />
      <div ref="carouselContentImage" :class="$style.carouselContentImage" />
    </div>
    <div
      :class="$style.carousel"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div
        v-for="(component, index) in content.components"
        :key="component.id"
        :class="[$style.carouselImage, {[$style.carouselImageActive]: activeIndex === index, [$style.carouselImagePrev]: previousIndex === index}]"
      >
        <div :class="$style.carouselContent">
          <lv-heading
            v-if="component.title"
            :class="[$style.carouselTitle, {
              [$style.carouselTitleSmallFont]: smallFont,
              [$style.carouselTitleRegularFont]: !smallFont
            }]"
            :tag="smallFont ? 'h3' :'h1'"
            :level="smallFont ? '3' : '1'"
            version2
          >
            {{ component.title }}
          </lv-heading>
          <div v-if="component.tags" :class="$style.carouselImageTagWrapper">
            <div
              v-for="componentTag in component.tags"
              :key="componentTag"
              :class="$style.carouselImageTag"
            >
              {{ componentTag }}
            </div>
          </div>
          <div :class="$style.carouselTextWrapper">
            <aw-text-block
              :class="[$style.carouselText, {[$style.carouselTextSmallFont]: smallFont}]"
              :content="component"
              :analytics-banner-interaction="analyticsBannerInteractionForBanner"
            />
          </div>
          <client-only>
            <aw-buttons
              v-bind="{
                isBtnInteractive (button) {
                  return true;
                },
                content: {
                  ...component,
                  button: {
                    ...component.button,
                    alignment: screenRange['tablet-min'] ? 'flex-start': 'center',
                  },
                },
                showTitle: false,
                analyticsBannerInteraction: analyticsBannerInteractionForBanner,
                analyticsMeasurable,
              }"
            />
          </client-only>
        </div>
        <div
          v-if="getContentImage(component).url"
          :class="[$style.imageGridArea, $style.carouselContentImage]"
        >
          <div :class="$style.carouselAspectRatioPadding" />
          <aw-buttons
            v-bind="{
              generateBtnLink (button) {
                return component.link || button.link;
              },
              isBtnInteractive (button) {
                return true;
              },
              content: {
                ...component,
                button: {
                  items: [
                    {},
                  ],
                  ...component.button,
                  alignment: 'stretch',
                },
              },
              limit: 1,
              showTitle: false,
              analyticsBannerInteraction: analyticsBannerInteractionForBanner,
              analyticsMeasurable,
              containerAttrs: {
                style: {
                  height: '100%',
                },
              },
              buttonAttrs: {
                styling: 'reset',
                stretch: true,
                style: {
                  height: '100%',
                },
              },
            }"
            :class="$style.carouselImageWrapperButtons"
          >
            <template #[buttonTemplate[index]]>
              <img
                :key="`image-${component.id}`"
                :src="getContentImage(component).url || null"
                :alt="getContentImage(component).alt"
                :loading="!isLazyImage && index === 0 ? 'eager': 'lazy'"
                :class="$style.carouselContentImageTag"
                style="height: 0;"
                @error="onImageError($event, component)"
              >
            </template>
          </aw-buttons>
        </div>
      </div>
    </div>

    <aw-carousel-v3-buttons
      v-if="contents.length > 1"
      :is-paused="paused"
      :class="$style.carouselNavigation"
      :aria-label-prev="`aw.cms.carousel_v3.w${aspectRatioWidth}_h${aspectRatioHeight}.prev`"
      :aria-label-toggle="`aw.cms.carousel_v3.w${aspectRatioWidth}_h${aspectRatioHeight}.toggle`"
      :aria-label-next="`aw.cms.carousel_v3.w${aspectRatioWidth}_h${aspectRatioHeight}.next`"
      @prev="prev"
      @toggle-pause="togglePause"
      @next="next"
    />
  </div>
</template>

<script>
  import { mapState } from 'pinia';
  import { defineAsyncComponent } from 'vue';
  import {
    createAwCarouselV3,
    createAwCarouselV3BannerAnalytics,
  } from '~~/common/mixins/awCarouselV3Mixin.js';
  import analyticsBannerInteractionMixin from '~~/common/mixins/analyticsBannerInteractionMixin.js';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index.mjs';
  import { useRuntimeConfig } from 'nuxt/app';

  export default {
    name: 'AwCarouselV3DynamicAspectRatio',
    components: {
      AwTextBlock: defineAsyncComponent(() => import('~~/common/components/AwStandardPartials/AwTextBlock')),
      AwButtons: defineAsyncComponent(() => import('~~/common/components/AwStandardPartials/AwButtons')),
      AwCarouselV3Buttons: defineAsyncComponent(() => import('~~/common/components/AwStandardPartials/AwCarouselV3/AwCarouselV3Buttons.vue')),
      LvHeading,
    },
    mixins: [analyticsBannerInteractionMixin],
    inject: ['getRowIndexById'],
    props: {
      content: {
        type: Object,
        required: true,
      },
      aspectRatioWidth: {
        type: Number,
        required: true,
      },
      aspectRatioHeight: {
        type: Number,
        required: true,
      },
      smallFont: {
        type: Boolean,
        default: false,
      },
    },
    setup (props) {
      const {
        paused,
        activeIndex,
        previousIndex,
        contents,
        next,
        prev,
        togglePause,
        handleMouseEnter,
        handleMouseLeave,
        onImageError,
        getContentImage,
      } = createAwCarouselV3({
        content: computed(() => props.content),
      });
      const { analyticsBannerInteraction } = createAwCarouselV3BannerAnalytics({
        contents,
        activeIndex,
        analyticsMeasurable: computed(() => props.analyticsMeasurable),
        analyticsBannerInteraction: computed(() => props.analyticsBannerInteraction),
      });
      return {
        paused,
        activeIndex,
        previousIndex,
        contents,
        next,
        prev,
        togglePause,
        handleMouseEnter,
        handleMouseLeave,
        onImageError,
        getContentImage,
        analyticsBannerInteractionForBanner: analyticsBannerInteraction,
      };
    },
    computed: {
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      imageAspectRatio () {
        return `calc(${this.aspectRatioHeight} / ${this.aspectRatioWidth})`;
      },
      buttonTemplate () {
        return this.content.components.map((component) => {
          return (component?.button?.items?.length || component.link) ? 'buttonText_0' : 'content';
        });
      },
      isLazyImage () {
        const limit = useRuntimeConfig().public.lazyLoadedComponentsLimit;
        if (!this.getRowIndexById) {
          return true;
        }
        return this.getRowIndexById(this.content.id) >= limit;
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.page {
  position: relative;
}

.carousel {
  position: relative;
  display: grid;


  &Image {

    display: grid;
    visibility: hidden;
    align-items: center;
    width: 100%;

    transition: opacity 0.8s, visibility 0.8s, transform 0.8s;
    text-align: center;
    opacity: 0;
    grid-area: 1 / 1;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px 8px;


    @include tablet(min) {
      text-align: left;
      grid-template-columns: repeat(8, 1fr);
      gap: 24px;
    }
    @include desktop-small(min) {
      grid-template-columns: repeat(12, 1fr);
      gap: 32px;
    }
    @include desktop-medium(min) {
      grid-template-columns: repeat(10, 1fr);
      gap: 40px;
    }


    &Active {
      visibility: visible;
      opacity: 1;

      .carouselContentImage, .carouselContent {
        transform: translateX(0);
        opacity: 1;
      }
    }

    &Prev:not(.carouselImageActive) {
      .carouselContentImage {
        transform: scale(0.6);
      }

      .carouselContent {
        transform: translateX(0);
      }
    }

    &Placeholder {
      position: absolute;
      visibility: hidden;
    }
  }

  &ImageTag {
    font-family: $secondary-font;
    font-size: 12px;
    font-weight: $font-weight-medium-v2;
    line-height: 16px;
    width: max-content;
    padding: 6px 8px;
    color: $color-text-primary;
    border-radius: 10px;
    background-color: rgba($color-background-1, 0.85);


    &Wrapper {
      display: flex;
      gap: 8px;
      justify-content: center;

      @include tablet(min) {
        justify-content: flex-start;
      }
    }
  }

  &Content {

    display: grid;
    justify-content: center;
    transition: transform 0.8s, opacity 0.8s;

    opacity: 0;
    color: $color-text-primary;
    grid-area: 2 / 1 / 3 / 3;
    gap: 20px;
    grid-template-rows: repeat(3, max-content);

    @include tablet(min) {
      justify-content: space-between;
      grid-area: 1 / 1 / 2 / 4;
    }

    @include desktop-small(min) {
      transform: translateX(-40px);
      grid-area: 1 / 1 / 2 / 5;
    }

    @include desktop-medium(min) {
      grid-area: 1 / 1 / 2 / 4;
    }
  }

  &ContentImage, &ContentImageTag {
    width: 100%;
    border-radius: 16px;

    @include tablet(min) {
      border-radius: 24px;
    }
  }

  &ContentImage {
    overflow: hidden;
    transition: transform 0.8s, scale 0.8s, opacity 0.8s;
    opacity: 0;
  }

  &ContentImageTag {
    height: 100%!important;
    object-fit: cover;
    object-position: center;
  }

  &Title {
    overflow: hidden;
    word-break: break-word;
    hyphens: auto;

    &RegularFont {
      font-size: $heading-1-font-size-mobile-version2 !important;
      line-height: $heading-1-line-height-mobile-version2 !important;
      max-height: calc($heading-1-line-height-mobile-version2 * 3);

      @include sidebar-desktop-medium-large(min) {
        font-size: $heading-1-font-size-desktop-version2 !important;
        line-height: $heading-1-line-height-desktop-version2 !important;
        max-height: calc($heading-1-line-height-desktop-version2 * 3);
      }
    }

    &SmallFont {
      @include desktop-small(max) {
        font-size: $heading-3-font-size-mobile-version2 !important;
        line-height: $heading-3-line-height-mobile-version2 !important;

        max-height: calc($heading-3-line-height-mobile-version2 * 3);
      }
      @include desktop-medium(min) {
        max-height: calc($heading-3-line-height-desktop-version2 * 3);
      }
    }
  }

  &Text {
    &Wrapper {
      min-height: 20px;
      @include desktop-small(min) {
        min-height: 24px;
      }
      @include desktop-medium(min) {
        margin-bottom: 20px;
      }
    }

    p {
      font-size: 16px;
      font-weight: $font-weight-bold-v2;
      line-height: 20px;
      overflow: hidden;

      max-height: calc(20px * 6);
      text-align: center;

      @include tablet(min) {
        text-align: left;
      }

      @include desktop-small(min) {
        font-size: 18px;
        line-height: 24px;
        max-height: calc(24px * 6);
      }
    }

    &SmallFont {
      p {
        font-size: 14px;
        font-weight: $font-weight-normal-v2;
        line-height: 20px;

        @include desktop-small(min) {
          font-size: 16px;
        }
      }
    }
  }

  &Navigation {
    display: grid;
    width: 128px;
    margin: 20px auto;
    padding: 8px;
    border: 1px solid $color-border;
    border-radius: 12px;
    background: $color-background-4;
    grid-template-columns: repeat(3, 32px);
    gap: 8px;

    @include tablet(min) {
      margin: 20px auto 40px;
    }
    @include desktop-medium(min) {
      margin: 40px auto;
    }
  }
}

.carouselAspectRatioPadding {
  padding-top: calc(100% * var(--aspectRatio));
}

.imageGridArea {

  @include mobile(max) {
    grid-area: 1 / 1 / 2 / 3;
    align-self: start;
  }

  @include tablet(min) {
    grid-area: 1/ 4 / 2 / 9;
  }

  @include desktop-small(min) {
    transform: translateX(40px);
    grid-area: 1 /5 / 2 / 13;
  }

  @include desktop-medium(min) {
    grid-area: 1/ 4 / 2 / 11;
  }
}

.carouselImageWrapperButtons {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
</style>
